import { type ComputedRef, computed, type Ref, reactive, watch } from 'vue'

/** What is wrong with a field and how to fix it, in plain words (no codes in Standard mode). */
export interface FieldIssue {
    message: string
    fix: string
}

/** One rule per field, checked from the value alone. Returns null when the value is fine. */
export type FieldRules<T> = { [K in keyof T]?: (value: T[K]) => FieldIssue | null }

export interface FieldValidation<T> {
    /** The error to show beside a field: null until the field was changed or left, and again once fixed. */
    error: (key: keyof T) => FieldIssue | null
    /** The user left the field: it may now show its error. */
    touch: (key: keyof T) => void
    /** True when no field breaks its rule, whether or not it was touched. */
    valid: ComputedRef<boolean>
    /** Save pressed: touches every field and returns the first invalid one (in rule order), or null. */
    validateAll: () => keyof T | null
    /**
     * A refusal only the server can decide (a taken slug, say), shown on its field with the fix until
     * the user next changes that field.
     */
    refuse: (key: keyof T, issue: FieldIssue) => void
    /** The form was filled again (opened, reloaded): no field shows an error until it is changed or left. */
    reset: () => void
    /** The id of a field's control, which its `ZhFieldError` and Save's focus use. */
    fieldId: (key: keyof T) => string
    /** Binds a control to its rule: id, `aria-invalid`, `aria-describedby` and touch on blur. */
    bind: (key: keyof T) => FieldBinding
    /** The props of the field's `ZhFieldError`. */
    message: (key: keyof T) => { id: string; message?: string; fix?: string }
    /** Save pressed: shows every error, focuses the first invalid control and returns its key, or null. */
    check: () => keyof T | null
}

/** What `bind` puts on a control. */
export interface FieldBinding {
    id: string
    'aria-invalid'?: 'true'
    'aria-describedby'?: string
    onBlur: () => void
}

export interface FieldValidationOptions {
    /** Prefixes every control id, so two forms on one page never share one. */
    idPrefix?: string
    /** A control that already has an id of its own (one other code reads) keeps it instead of a prefixed one. */
    ids?: Record<string, string>
}

/** Moves focus to a control by its id (the controls are kit components, so a template ref is not the element). */
export function focusById(id: string): void {
    const element = document.getElementById(id)
    if (element instanceof HTMLElement) element.focus()
}

/**
 * Runs a form's existing rules while the user types (CTO decision 3 on #1757): a field shows its error
 * only after it was changed or left, is checked again on every change, and Save shows every error at
 * once. The rules come from the form; this adds none.
 */
export function useFieldValidation<T extends Record<string, unknown>>(
    values: Ref<T>,
    rules: FieldRules<T>,
    options: FieldValidationOptions = {},
): FieldValidation<T> {
    const keys = Object.keys(rules) as (keyof T & string)[]
    const prefix = options.idPrefix ?? 'field'
    const touched = reactive<Record<string, boolean>>({})
    const refused = reactive<Record<string, FieldIssue | undefined>>({})

    const issueOf = (key: keyof T): FieldIssue | null => rules[key]?.(values.value[key]) ?? null
    const snapshot = (): Record<string, string> => {
        const out: Record<string, string> = {}
        for (const key of keys) out[key] = JSON.stringify(values.value[key]) ?? ''
        return out
    }

    let previous = snapshot()
    watch(
        values,
        () => {
            const next = snapshot()
            for (const key of keys) {
                if (next[key] === previous[key]) continue
                touched[key] = true
                delete refused[key]
            }
            previous = next
        },
        { deep: true, flush: 'sync' },
    )

    const error = (key: keyof T): FieldIssue | null =>
        refused[key as string] ?? (touched[key as string] ? issueOf(key) : null)
    const touch = (key: keyof T): void => {
        touched[key as string] = true
    }
    const validateAll = (): keyof T | null => {
        for (const key of keys) touched[key] = true
        return keys.find(key => issueOf(key) !== null) ?? null
    }
    const fieldId = (key: keyof T): string => options.ids?.[String(key)] ?? `${prefix}-${String(key)}`

    return {
        error,
        touch,
        valid: computed(() => keys.every(key => issueOf(key) === null)),
        validateAll,
        refuse: (key, issue) => {
            refused[key as string] = issue
        },
        reset: () => {
            for (const key of Object.keys(touched)) delete touched[key]
            for (const key of Object.keys(refused)) delete refused[key]
            previous = snapshot()
        },
        fieldId,
        bind: key => {
            const shown = error(key) !== null
            return {
                id: fieldId(key),
                'aria-invalid': shown ? 'true' : undefined,
                'aria-describedby': shown ? `${fieldId(key)}-error` : undefined,
                onBlur: () => touch(key),
            }
        },
        message: key => {
            const issue = error(key)
            return { id: `${fieldId(key)}-error`, message: issue?.message, fix: issue?.fix }
        },
        check: () => {
            const first = validateAll()
            if (first !== null) focusById(fieldId(first))
            return first
        },
    }
}
